import React from 'react'
import {useBreakpointValue} from '@chakra-ui/react'
import {
  Box,
  Flex,
  MenuDivider,
  MenuItem,
  Spinner,
} from '../../utils/chakraMonomorphic'
import {GalleryImage} from './GalleryImage'
import {FilenameOverlay} from './FilenameOverlay'
import {PhotoGalleryActions} from './types'
import {SnowmanMenu} from '../SnowmanMenu/SnowmanMenu'
import {photoAnchorId} from '../../utils/photoDeepLink'

// The thumbnail cache-bust must change ONLY when the underlying image bytes
// change — i.e. after a rotation or a reprocess. It must NEVER depend on
// react-photo-gallery's `photo.height`/`photo.width`, which are fractional,
// container-width-derived layout values recomputed on every ResizeObserver
// tick. Baking those (plus a stringified `undefined` from a missing
// `processing`) into the URL made every re-layout mint a brand-new `src`, so
// the <img> refetched the thumbnail in an unbounded loop (~9.5 req/s), which
// presented as constant flickering/reloading on album grids (AB#22131).
export const buildThumbnailCacheBustSrc = (photo: {
  src: string
  rotation?: 0 | 90 | 180 | 270
  processing?: boolean
}): string =>
  `${photo.src}&cachebust=${photo.rotation ?? 0}-${photo.processing ? 1 : 0}`

interface GalleryImageCardProps {
  href?: string
  fallbackPhoto: string
  actions: PhotoGalleryActions[]
  testID?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  photo: any
  margin: number
  onClick?: () => void
}

export const GalleryImageCard: React.FC<GalleryImageCardProps> = ({
  fallbackPhoto,
  actions,
  photo,
  margin,
  testID,
  href,
  onClick,
}) => {
  const lightboxLinkRef = React.useRef<HTMLImageElement>(null)
  const [isOverlayVisible, setIsOverlayVisible] = React.useState(false)
  const isMobile = useBreakpointValue({base: true, sm: true, md: false})

  const GalleryImageComponent = (
    <GalleryImage
      src={buildThumbnailCacheBustSrc(photo)}
      srcHeight={photo.height}
      srcWidth={photo.width}
      margin={margin}
      fallbackPhoto={fallbackPhoto}
      rotation={photo.rotation}
      processing={photo.processing}
      clickRef={lightboxLinkRef}
    />
  )

  // A stable DOM anchor so an album deep link (`?photo=`) has something to
  // scroll to. See utils/photoDeepLink.ts. `photo.key` is a ResourceID (a
  // string holding a numeric photo id), coerced here so the anchor id is built
  // from the same canonical number the `?photo=` param parses to — the two
  // sides must agree exactly or the deep link silently finds nothing. Omitted
  // rather than emitting `album-photo-NaN` if a non-numeric key ever appears.
  const anchorPhotoId = Number(photo.key)
  const anchorId = Number.isInteger(anchorPhotoId)
    ? photoAnchorId(anchorPhotoId)
    : undefined

  return (
    <Flex
      position='relative'
      flexDir='row'
      alignItems='flex-start'
      id={anchorId}
      data-testid={testID}
      onMouseEnter={(): void => setIsOverlayVisible(true)}
      onMouseLeave={(): void => setIsOverlayVisible(false)}
      key={photo.key}>
      {photo.processing ? (
        GalleryImageComponent
      ) : (
        <a
          href={href}
          data-attribute='SRL'
          onClick={(event) => {
            event.preventDefault()
            onClick?.()
          }}>
          {GalleryImageComponent}

          <FilenameOverlay
            visible={isOverlayVisible || !!isMobile}
            filename={photo.originalFileName}
            margin={margin}
            clickRef={lightboxLinkRef}
          />
        </a>
      )}

      <Box
        justifyContent='center'
        alignItems='flex-start'
        position='absolute'
        right='0'
        top='0'
        zIndex='5'
        mr={2}
        mt={3}>
        <SnowmanMenu placement='auto'>
          {actions
            .filter(
              (action) =>
                // disable delete and make album cover if processing
                !(
                  photo.processing &&
                  (action.name === 'Delete' ||
                    action.name === 'Make Album Cover')
                ),
            )
            .map((action, actionIndex, actionsArr) => (
              <React.Fragment key={`photo-${action.name}`}>
                <MenuItem onClick={action.onClick(photo.key)}>
                  {action.name}
                </MenuItem>
                {actionIndex < actionsArr.length - 1 ? (
                  <MenuDivider borderBottom='.1rem solid' />
                ) : null}
              </React.Fragment>
            ))}
        </SnowmanMenu>
      </Box>

      {photo.processing && (
        <Flex
          position='absolute'
          margin='10px'
          data-testid='processing-spinner'>
          <Spinner color='white' size='md' thickness='5px' />
        </Flex>
      )}
    </Flex>
  )
}
